// Utility functions for converting images to chart data

/**
 * Convert hex color to RGB
 */
function hexToRgb(hex) {
  const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
  return result ? {
    r: parseInt(result[1], 16),
    g: parseInt(result[2], 16),
    b: parseInt(result[3], 16)
  } : null;
}

/**
 * Convert RGB to hex
 */
function rgbToHex(r, g, b) {
  return "#" + ((1 << 24) + (r << 16) + (g << 8) + b).toString(16).slice(1);
}

/**
 * Calculate color distance between two RGB colors
 */
function colorDistance(rgb1, rgb2) {
  const dr = rgb1.r - rgb2.r;
  const dg = rgb1.g - rgb2.g;
  const db = rgb1.b - rgb2.b;
  return Math.sqrt(dr * dr + dg * dg + db * db);
}

/**
 * Find the closest color in the palette
 */
function findClosestColor(targetRgb, palette) {
  let closestIndex = 0;
  let minDistance = Infinity;
  
  palette.forEach((color, index) => {
    const paletteRgb = hexToRgb(color);
    if (paletteRgb) {
      const distance = colorDistance(targetRgb, paletteRgb);
      if (distance < minDistance) {
        minDistance = distance;
        closestIndex = index;
      }
    }
  });
  
  return closestIndex;
}

/**
 * Get the average color of a region in image data
 */
function getAverageColor(imageData, x, y, width, height, canvasWidth, canvasHeight) {
  let r = 0, g = 0, b = 0, count = 0;
  
  const startX = Math.max(0, Math.floor(x));
  const endX = Math.min(canvasWidth, Math.ceil(x + width));
  const startY = Math.max(0, Math.floor(y));
  const endY = Math.min(canvasHeight, Math.ceil(y + height));
  
  for (let py = startY; py < endY; py++) {
    for (let px = startX; px < endX; px++) {
      const index = (py * canvasWidth + px) * 4;
      r += imageData.data[index];
      g += imageData.data[index + 1];
      b += imageData.data[index + 2];
      count++;
    }
  }
  
  if (count === 0) return { r: 255, g: 255, b: 255 };
  
  return {
    r: Math.round(r / count),
    g: Math.round(g / count),
    b: Math.round(b / count)
  };
}

/**
 * Convert image to chart data
 */
export function convertImageToChart(imageElement, imageTransform, rows, cols, cellSize, colors, mode = 'existing') {
  // Create a temporary canvas to work with the image
  const tempCanvas = document.createElement('canvas');
  const tempCtx = tempCanvas.getContext('2d');
  
  // Set canvas size to match the chart dimensions exactly
  const chartWidth = cols * cellSize;
  const chartHeight = rows * cellSize;
  tempCanvas.width = chartWidth;
  tempCanvas.height = chartHeight;
  
  // Clear with white background
  tempCtx.fillStyle = 'white';
  tempCtx.fillRect(0, 0, chartWidth, chartHeight);
  
  // Draw the transformed image onto the temporary canvas
  tempCtx.save();
  tempCtx.translate(imageTransform.x, imageTransform.y);
  tempCtx.scale(imageTransform.scale, imageTransform.scale);
  
  // Draw the image at its natural size - the transform scale already handles sizing
  tempCtx.drawImage(imageElement, 0, 0);
  tempCtx.restore();
  
  // Get image data from the temporary canvas
  const imageData = tempCtx.getImageData(0, 0, chartWidth, chartHeight);
  
  // Convert to chart data
  const chartData = [];
  const newColors = [...colors];
  const usedColors = new Set();
  
  for (let row = 0; row < rows; row++) {
    chartData[row] = [];
    for (let col = 0; col < cols; col++) {
      // Calculate the region for this cell
      const cellX = col * cellSize;
      const cellY = row * cellSize;
      
      // Get average color for this cell
      const avgColor = getAverageColor(imageData, cellX, cellY, cellSize, cellSize, chartWidth, chartHeight);
      
      let colorIndex;
      
      if (mode === 'existing') {
        // Find closest existing color
        colorIndex = findClosestColor(avgColor, colors);
      } else {
        // Add new colors mode
        const hexColor = rgbToHex(avgColor.r, avgColor.g, avgColor.b);
        
        // Check if this color already exists in our palette
        let existingIndex = newColors.findIndex(color => color.toLowerCase() === hexColor.toLowerCase());
        
        if (existingIndex === -1) {
          // Check if it's close to an existing color (to avoid too many similar colors)
          let closeIndex = -1;
          let minDistance = Infinity;
          
          newColors.forEach((color, index) => {
            const colorRgb = hexToRgb(color);
            if (colorRgb) {
              const distance = colorDistance(avgColor, colorRgb);
              if (distance < 30 && distance < minDistance) { // Threshold for "similar" colors
                minDistance = distance;
                closeIndex = index;
              }
            }
          });
          
          if (closeIndex !== -1) {
            colorIndex = closeIndex;
          } else {
            // Add new color to palette
            newColors.push(hexColor);
            colorIndex = newColors.length - 1;
          }
        } else {
          colorIndex = existingIndex;
        }
      }
      
      chartData[row][col] = colorIndex;
      usedColors.add(colorIndex);
    }
  }
  
  return {
    data: chartData,
    colors: newColors,
    usedColorCount: usedColors.size
  };
}

/**
 * Load image from URL and return a promise with the image element
 */
export function loadImage(url) {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => resolve(img);
    img.onerror = reject;
    img.crossOrigin = 'anonymous'; // Enable CORS if needed
    img.src = url;
  });
}
